import React, { useState } from 'react'
import { createStructuredSelector } from 'reselect'
import classnames from 'classnames'
import Formsy from 'formsy-react'

import Modal from '@components/Modal'
import Button from '@components/Button'
import Checkbox from '@components/Checkbox'
import FormsyInput from '@components/Textfield/withFormsy'
import ErrorMessage from '@components/ErrorMessage'
import { withTranslate } from '@i18n/utils'

import VehicleTabs from 'dashboard-subscriptions/components/ParkersTab/VehicleTabs'
import { assignParker } from 'dashboard-subscriptions/actions'
import { assignedParkerSelector, assignParkerPendingSelector } from 'dashboard-subscriptions/selectors'
import { ASSIGN_PARKER_PROGRESS } from 'dashboard-subscriptions/constants'

import { userSelector } from 'auth/selectors'
import useRequestPending from 'shared/hooks/useRequestPending'

import styles from './styles.module.scss'
import featureToggles from 'shared/featureToggles'

const AssignParkerModal = ({
    assignParker,
    pending,
    subscriptionId,
    closeModal,
    user: { details },
    translate: t,
    assignedParker,
}) => {
    const { selfServiceConfirmed, memberId } = assignedParker
    const { error, isLoading } = useRequestPending(pending, closeModal, ASSIGN_PARKER_PROGRESS)
    const [userData, setUserData] = useState({
        firstName: assignedParker.firstName,
        lastName: assignedParker.lastName,
        emailAddress: assignedParker.emailAddress,
    })
    const [selectedVehicles, setSelectedVehicles] = useState(assignedParker.vehicles || [])
    const [useUserDetails, setUseUserDetails] = useState(false)

    const enableEditParker = !featureToggles.hasSubcriptionLite
    const [enableSelfService, setEnableSelfService] = useState(enableEditParker && selfServiceConfirmed)

    const changeUserData = e => {
        const { name, value } = e.currentTarget
        setUserData(prevState => ({ ...prevState, [name]: value }))
    }

    const submitParker = () => {
        assignParker({
            user: useUserDetails ? details : userData,
            selfService: enableSelfService,
            subscriptionId,
            selectedVehicles,
            memberId,
        })
    }

    return (
        <Modal className={styles.assignParkerModal} size="large" onClose={closeModal}>
            <Formsy onSubmit={submitParker}>
                <>
                    <h5 className={classnames('text-left', 'my-4', styles.title)}>
                        <T>dashboard.subscriptions.assign.parker.modal.title</T>
                    </h5>
                    {enableEditParker && (
                        <div className="d-sm-flex mt-3">
                            {!enableSelfService && (
                                <Checkbox checked={useUserDetails} onClick={() => setUseUserDetails(!useUserDetails)}>
                                    <T>dashboard.subscriptions.assign.parker.label.my.details</T>
                                </Checkbox>
                            )}
                            {ENABLE_SELF_SERVICE && !useUserDetails && (
                                <Checkbox
                                    disabled={selfServiceConfirmed}
                                    checked={enableSelfService}
                                    onClick={() => setEnableSelfService(!enableSelfService)}>
                                    <T>dashboard.subscriptions.assign.parker.label.self.service</T>
                                </Checkbox>
                            )}
                        </div>
                    )}
                    <div className="text-left">
                        <span className={classnames('mb-3', 'mt-3', styles.columnLabel)}>
                            <T>dashboard.subscriptions.assign.parker.label.parker</T>
                        </span>

                        <div className="d-md-flex">
                            <FormsyInput
                                placeholder={t('dashboard.account.firstname')}
                                value={useUserDetails ? details.firstName : userData.firstName}
                                onChange={changeUserData}
                                validationError={t('dashboard.account.firstName.invalid')}
                                className={classnames('mr-3', styles.parkerInput)}
                                name="firstName"
                                required
                                disabled={useUserDetails}
                            />
                            <FormsyInput
                                placeholder={t('dashboard.account.lastname')}
                                onChange={changeUserData}
                                value={useUserDetails ? details.lastName : userData.lastName}
                                validationError={t('dashboard.account.lastName.invalid')}
                                disabled={useUserDetails}
                                className={styles.parkerInput}
                                name="lastName"
                                required
                            />
                        </div>
                        {enableSelfService && (
                            <FormsyInput
                                placeholder={t('dashboard.account.email')}
                                onChange={changeUserData}
                                name="emailAddress"
                                value={userData.emailAddress}
                                className={classnames('col-sm-6', 'mt-3', 'pl-0', 'pr-3', 'mx-0', styles.parkerInput)}
                                required={enableSelfService}
                                validationError={t('dashboard.account.emailAddress.invalid')}
                                disabled={selfServiceConfirmed}
                            />
                        )}

                        <VehicleTabs
                            disabled={selfServiceConfirmed}
                            vehicles={selectedVehicles}
                            setSelectedVehicles={setSelectedVehicles}
                        />
                    </div>
                    <div className="d-flex justify-content-end mt-3">
                        <Button btnStyle="outline" onClick={closeModal} disabled={isLoading}>
                            <T>dashboard.subscriptions.modal.close</T>
                        </Button>
                        <Button
                            disabled={selectedVehicles?.length === 0 || isLoading}
                            isLoading={isLoading}
                            btnStyle="primary"
                            type="submit">
                            <T>dashboard.account.save</T>
                        </Button>
                    </div>
                </>
                {error && <ErrorMessage messages={[error]} className="mt-3" />}
            </Formsy>
        </Modal>
    )
}

const mapStateToProps = (state, ownProps) =>
    createStructuredSelector({
        assignedParker: assignedParkerSelector(ownProps.subscriptionId, ownProps.member.id),
        user: userSelector,
        pending: assignParkerPendingSelector,
    })

export default compose(withTranslate, connect(mapStateToProps, { assignParker }))(AssignParkerModal)
